import * as React from "react";
import { $, merge, style } from "glamor";
import { IToolPanelParams } from "ag-grid-community";
import Button from "components/form/button";
import Divider from "@material-ui/core/Divider";
import Dialog from "components/dialog";
import TextField from "components/form/text-field";
import { Form } from "formsy-react";
import Typography from "@material-ui/core/Typography";
import { formatMessage } from "lib/globalize-util";
import { connect, Dispatch } from "react-redux";
import {
    addItemDisplayPreferences,
    changeCurrentView,
    DEFAULT_MASTER_VIEW_NAME,
    fetchRollupObject,
    IRollupObjectRequest,
    editItemDisplayPreference,
    removeItemDisplayPreferences,
    fetchMerchantItemDisplayPreferences,
    promoteItemDisplayPreference,
    setViewToDefault,
    editMerchantItemDisplayPreference,
    editMerchantLocalItemDisplayPreference,
    deleteMerchantItemDisplayPreference,
} from "actions/collections";
import { IStore } from "reducers";
import { IAction } from "epics/base-epic";
import { v4 as uuid } from "uuid/v4";
import { List, Map, fromJS } from "immutable";
import { withRouter } from "react-router";
import { theme } from "styles/theme";
import { useDebounce } from "hooks/useDebounce";
import { ISchemaKey, mergeWithDefaultSchema } from "helpers/product-collection";
import { DATATEST } from "constants/integration";
import {
    IDisplayPreference,
    IGridParams,
    IGridParamsQueryParam,
    IMerchantDisplayPreference,
    parseMetrics,
    transformAllocationDimensionForGrid,
    transformAllocationDimensionForUpdate,
    transformDoorGroupMetricsForUpdate,
    transformGridOrderToServer,
    transformGridParamsForGrid,
    transformGridParamsForUpdate,
} from "./grid-param-helpers";
import { DimensionOrder, IServerDimensionPreference, DimensionProps, Order } from "helpers/door-collection";
import { getAllocationDimensionsBySubtype } from "reducers/selectors/collection";
import {
    AllocationDimensionKind,
    IAllocationDimension,
    ROLLUP_ALLOCATION_DIMENSION_DELIMITER,
} from "helpers/allocation-dimension";
import * as SentryUtil from "lib/sentry-util";
import { amphibiousToJS } from "helpers/objUtils";
import { AssortmentEvents, AssortmentTransactions } from "constants/sentry-metrics";
import { SavedViewsActions } from "constants/analytics";
import { CollectionKeys, IMAGE_COLUMN_MIN_WIDTH, ROW_HEIGHT } from "constants/product-collection";
import lodashCloneDeep from "lodash/cloneDeep";
import { DoorGroupMetric, Metrics } from "helpers/metrics/types";
import { Dimension, DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { parseMetricSchema } from "reducers/dimensions-panel/draft";
import { WindowAnalytics } from "helpers/window-analytics";
import { SavedViews, SavedViewsByCollection } from "components/product-collection/product-collection-views/shared";
import { IImageCellDimensions } from "constants/types";
import me from "models/me";

export const DEFAULT_VIEW_NAME = "Default";
export const CollectionSavedViewRollupSeparator = "##";
export const ReviewTabSavedViewRollupSeparator = "###";

const makeDimensionActiveValues = (
    currentDimensionPreference: Dimension
): { values: Array<{ id: string }>; order: DimensionOrder } | null =>
    !currentDimensionPreference.selected
        ? null // when there is no active values backend expects null, since and empty list means all itens selected
        : {
              values: (currentDimensionPreference.values ?? [])
                  .filter((value) => value.selected)
                  .map((value) => ({
                      id: value.id,
                  })),
              order: transformGridOrderToServer(currentDimensionPreference.sort),
          };

export const mergeDimensionPreference = (
    allocationDimensions: IAllocationDimension[],
    existingDimensionParams: DimensionsPanelReduxState,
    defaultDimensionPreferences: IServerDimensionPreference | null
) => {
    if (!defaultDimensionPreferences) {
        return null;
    }

    const mutatedDimensionPreferences = JSON.parse(JSON.stringify(defaultDimensionPreferences));

    const uniqueRawAllocationDimensionIds = allocationDimensions.reduce((acc, defaultAllocationDimension) => {
        const rawAllocationDimensionId = defaultAllocationDimension.id.split(ROLLUP_ALLOCATION_DIMENSION_DELIMITER)[0];
        if (!acc.includes(rawAllocationDimensionId)) {
            acc.push(rawAllocationDimensionId);
        }

        return acc;
    }, []);

    // Valid default dimension preferences should have same length as allocation dimensions
    if (mutatedDimensionPreferences.dimensions?.length !== uniqueRawAllocationDimensionIds.length) {
        return null;
    }

    for (let i = 0; i < mutatedDimensionPreferences.dimensions.length; i++) {
        const mutatedDimensionPreference = mutatedDimensionPreferences.dimensions[i];

        const currentDimensionPreference = existingDimensionParams.dimensions[i];

        // Dimension is considered modified when:
        const isDimensionModified =
            // - Dimension is unselected
            !currentDimensionPreference.selected ||
            // - Sort neither Asc nor undefined
            (currentDimensionPreference.sort && currentDimensionPreference.sort !== Order.Ascending) ||
            // - Some values are unselected
            currentDimensionPreference.values.some((value) => !value.selected);

        if (isDimensionModified) {
            mutatedDimensionPreference[DimensionProps.ActiveValues] =
                makeDimensionActiveValues(currentDimensionPreference);
        }

        // Skip below block for non-Door level dimension
        if (i !== mutatedDimensionPreferences.dimensions.length - 1) {
            continue;
        }

        // Take existing "show doors" value when it's true
        if (existingDimensionParams.showDoors) {
            mutatedDimensionPreference[DimensionProps.Show] = existingDimensionParams.showDoors;
        }

        // Take existing "show total" value when it's true
        if (existingDimensionParams.metrics.showAssortmentTotals) {
            mutatedDimensionPreference[DimensionProps.ShowTotals] =
                existingDimensionParams.metrics.showAssortmentTotals;
        }

        // Take existing "show plan" value when it's true
        if (existingDimensionParams.metrics.showAssortmentPlan) {
            mutatedDimensionPreference[DimensionProps.ShowPlan] = existingDimensionParams.metrics.showAssortmentPlan;
        }

        // Take existing "attributes" any attribute is selected
        if (existingDimensionParams.attributes.some((attribute) => attribute.selected)) {
            mutatedDimensionPreference[DimensionProps.ActiveAttributes] = {
                attributes: existingDimensionParams.attributes.reduce((acc, existingAttribute) => {
                    if (existingAttribute.selected) {
                        acc.push({
                            field_name: existingAttribute.id,
                            values: (existingAttribute.values ?? [])
                                .filter((value) => value.selected)
                                .map((value) => ({
                                    string_value: value.id,
                                })),
                            order: transformGridOrderToServer(existingAttribute.sort),
                        });
                    }
                    return acc;
                }, []),
            };
        }
    }

    return mutatedDimensionPreferences;
};

const isPreferenceLoaded = (preference) => {
    const preferenceProperties = ["display_preferences", "grid_parameters"];
    return preferenceProperties.every((prop) => preference[prop]);
};

export interface IStateProps {
    itemDisplayPreferences: Array<Map<string, any>>;
    merchantItemDisplayPreferences: Array<Map<string, any>>;
    merchantId: string;
    displayPreferencesId: string;
    merchantDisplayPreferencesId: string;
    currentView?: List<Map<keyof ISchemaKey, any>>;
    currentViewName?: string;
    itemDisplayProperty: List<Map<keyof ISchemaKey, any>>;
    defaultAllocationDimensions: IAllocationDimension[];
    merchantAllocationDimensions?: IServerDimensionPreference;
    doorGroupMetricsSchema: List<DoorGroupMetric>;
    rollupViews?: { [key: string]: Map<string, any> };
    dimensionsPanelApplied: DimensionsPanelReduxState;
    switchingTile?: boolean;
    handleSwitchingTileAndGrid?: (switching: boolean) => void;
}

export interface IDispatchProps {
    onAdd: (id: string, displayPreference: IDisplayPreference, index: number) => IAction<any>;
    onRemove: (id: string, index: number) => IAction<any>;
    onUpdate: (id: string, index: number, displayPreference: IDisplayPreference) => IAction<any>;
    onMerchantUpdate: (merchantId: string, id: string, displayPreference: IMerchantDisplayPreference) => IAction<any>;
    onMerchantDelete: (collectionId: string, merchantId: string, id: string, index: number) => IAction<any>;
    onFetchMerchantPreferences: (merchantId: string) => IAction<any>;
    onMerchantLocalUpdate: (id: string, index: number, displayPreference: IDisplayPreference) => IAction<any>;
    onPromote: (collectionId: string, merchantId: string, index: number) => IAction<any>;
    onChangeCurrentView: (name: string, data?: List<Map<string, any>>) => IAction<any>;
    onSetViewToDefault: () => IAction<any>;
    onFetchRollupItemDisplayPreferences: (params: IRollupObjectRequest) => IAction<any>;
}

export interface IOwnProps extends IToolPanelParams {
    collectionId: string;
    onViewParamsChange: (
        gridParams: IGridParamsQueryParam,
        updatedDimensionsState: DimensionsPanelReduxState,
        search: string,
        rerenderColumns?: boolean
    ) => void;
    getGridParams: () => IGridParamsQueryParam;
    onClearViewParams: () => void;
    onSetImageCellDimensions: (dimensions: IImageCellDimensions) => void;
    router: any;
    editable: boolean;
    isRollupMode: boolean;
}

interface SavedViewUpdateInfo {
    viewId: string;
    viewName: string;
    viewIndex: number;
}

type IProps = IOwnProps & IDispatchProps & IStateProps;

export const DataViewPanelComponent: React.FunctionComponent<IProps> = ({
    itemDisplayPreferences,
    merchantItemDisplayPreferences,
    collectionId,
    merchantId,
    displayPreferencesId,
    merchantDisplayPreferencesId,
    onAdd,
    onRemove,
    onSetViewToDefault,
    onUpdate,
    onMerchantUpdate,
    onMerchantDelete,
    onFetchMerchantPreferences,
    onMerchantLocalUpdate,
    onPromote,
    currentViewName,
    currentView,
    onChangeCurrentView,
    onViewParamsChange,
    getGridParams,
    router,
    editable,
    itemDisplayProperty,
    onClearViewParams,
    defaultAllocationDimensions,
    isRollupMode,
    merchantAllocationDimensions,
    doorGroupMetricsSchema,
    onFetchRollupItemDisplayPreferences,
    rollupViews,
    dimensionsPanelApplied,
    onSetImageCellDimensions,
    switchingTile,
    handleSwitchingTileAndGrid,
}) => {
    //TODO move outside the component
    const styles = React.useMemo(
        () =>
            style({
                width: "100%",
                " .menuList": {
                    paddingTop: "0",
                },
                " .newView": {
                    width: "100%",
                    height: "64px",
                    display: "flex",
                },
                " .newView .newViewButton": {
                    margin: "auto !important",
                    height: "40px",
                    width: "167px",
                    borderRadius: "24px",
                    borderColor: "#bdbdbd",
                    color: "#585858",
                    backgroundColor: "#fff",
                    textTransform: "none",
                    fontFamily: "Lato",
                    fontSize: "14px",
                    fontWeight: "normal",
                    fontStyle: "normal",
                    fontStretch: "normal",
                    lineHeight: "normal",
                    letterSpacing: "0.18px",
                    textAlign: "center",
                    paddingTop: "11px",
                    paddingBottom: "12px",
                },
                " .view": {
                    minHeight: "40px !important",
                    height: "40px",
                },
                " .view:hover": {
                    minHeight: "auto",
                    height: "auto",
                },
                " .viewEntry": {
                    height: "40px",
                    lineHeight: "40px",
                    display: "flex",
                    width: "167px",
                },
                " .viewEntry:hover": {
                    height: "auto",
                },
                " .viewName": {
                    width: "119px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    display: "inline-block",
                    textOverflow: "ellipsis",
                    fontSize: "11px",
                    padding: "13px 0",
                    lineHeight: "normal",
                },
                " .viewName:hover": {
                    whiteSpace: "normal",
                    overflow: "unset",
                    lineHeight: "normal",
                    wordBreak: "break-word",
                },
                " .view > div": {
                    display: "flex",
                },
                " .selectedView": {
                    backgroundColor: `${theme.palette.grey} !important`,
                },
                " .viewButtons": {
                    paddingTop: "3px",
                },
                " .view .iconButton": {
                    opacity: 0,
                    width: "20px !important",
                    height: "20px !important",
                    padding: "0px !important",
                },
                " .view .iconButton > div": {
                    height: "20px !important",
                },
                " .view .iconButton svg": {
                    width: "20px !important",
                    height: "20px !important",
                    color: `${theme.palette.darkGrey} !important`,
                },
                " .view:hover .iconButton": {
                    opacity: 1,
                    display: "block",
                    backgroundColor: "transparent",
                },
                " .notifyIconButton": {
                    opacity: 0,
                    backgroundColor: "transparent",
                    width: "20px !important",
                    height: "20px !important",
                    padding: "0px !important",
                },
                " .notifyIconButtonDisplay": {
                    opacity: 1,
                },
                " .notifyIconButton > div": {
                    height: "20px !important",
                },
                " .notifyIconButton svg": {
                    width: "20px !important",
                    height: "20px !important",
                    color: `${theme.palette.darkGrey} !important`,
                },
                " button, li, .MuiTypography-body1": {
                    fontFamily: "Lato, sans-serif !important",
                },
            }),
        []
    );
    //TODO move outside
    const dialogStyle = React.useMemo(() => {
        return merge(
            {
                width: "400px !important",
            },
            $(" .btnCancel", {
                marginRight: "10px !important",
            }),
            $(" .removedModalBody", {
                paddingTop: "10px",
            }),
            $(" .saveViewBody", {
                height: "100px",
            }),
            $(" .removedFields", {
                backgroundColor: theme.palette.lightestGrey,
                padding: "10px",
                textAlign: "center",
                marginTop: "10px",
                fontWeight: 600,
                fontFamily:
                    'Font family -apple-system, system-ui, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
                color: "rgba(0, 0, 0, 0.54)",
            })
        );
    }, []);

    const [isEditOpen, setIsEditOpen] = React.useState<boolean>(false);
    const [editedViewName, setEditedViewName] = React.useState<string>(currentViewName || "");
    const [viewNameUnique, setViewNameUnique] = React.useState<boolean>(true);
    const [schemaIdWithRemovedFields, setSchemaIdWithRemovedFields] = React.useState<string>();
    const [removedSchemaFields, setRemovedSchemaFields] = React.useState<string[]>([]);
    const [isRemovedFieldsModalOpen, setIsRemovedFieldsModalOpen] = React.useState<boolean>(false);
    const [confirmPromoteSavedViewModalInfo, setConfirmPromoteSavedViewModalInfo] =
        React.useState<SavedViewUpdateInfo | null>(null);
    const [confirmUpdateSavedViewModalInfo, setUpdateSavedViewModalInfo] = React.useState<SavedViewUpdateInfo | null>(
        null
    );
    const [confirmDeleteSavedViewModalInfo, setDeleteSavedViewModalInfo] = React.useState<SavedViewUpdateInfo | null>(
        null
    );
    const [enterKeyDown, setEnterKeyDown] = React.useState<string>("false");
    const [saveButtonClicked, setSaveButtonClicked] = React.useState<string>("false");
    const [displayPreferences, setDisplayPreferences] = React.useState(itemDisplayPreferences);
    const [merchantDisplayPreferences, setMerchantDisplayPreferences] = React.useState(merchantItemDisplayPreferences);
    const [fetchedMerchantDisplayPreferences, setFetchedMerchantDisplayPreferences] = React.useState(false);
    const [mergedMerchantDisplayPreferences, setMergedDisplayPreferences] = React.useState(false);
    const [requestedView, setRequestedView] = React.useState<{
        viewName: string;
        viewId: string;
        viewIndex: string | number;
        collectionId: string;
    }>();
    const debouncedEnterKeyDown = useDebounce(enterKeyDown, 1000);
    const debouncedSaveButtonClicked = useDebounce(saveButtonClicked, 1000);
    const handleSaveNewRef = React.useRef<() => void>();
    const queryViewId = (router.location.query || {}).viewId;

    const metricsFromSchema = React.useMemo(() => {
        return parseMetricSchema(doorGroupMetricsSchema?.toJS() || []);
    }, [doorGroupMetricsSchema]);
    const isMetricsEnabled = metricsFromSchema.length > 0;

    React.useEffect(() => {
        if (Array.isArray(itemDisplayPreferences)) {
            setDisplayPreferences(fromJS(itemDisplayPreferences));
        } else {
            setDisplayPreferences(itemDisplayPreferences);
        }
    }, [itemDisplayPreferences]);

    React.useEffect(() => {
        setMerchantDisplayPreferences(merchantItemDisplayPreferences);
    }, [merchantItemDisplayPreferences]);

    const [currentViewId, viewList, merchantViewList, viewListByCollection] = React.useMemo(() => {
        const viewListByCollection = [];
        let defaultViewId;

        const views =
            displayPreferences?.map((displayPreference) => {
                const name = displayPreference.get("name");
                const id = displayPreference.get("id");
                const collectionId = displayPreference.get("collectionId");
                const collectionName = displayPreference.get("collectionName");
                const view = {
                    ...displayPreference.toJS(),
                    lowerCaseName: name.toLowerCase(),
                    preference: displayPreference,
                    collectionId,
                    name,
                    id,
                };

                if (!isRollupMode && view.name === DEFAULT_VIEW_NAME) {
                    defaultViewId = view.id;
                }

                if (isRollupMode) {
                    const currentViewGroup = viewListByCollection.find((item) => item.id === collectionId);

                    if (currentViewGroup) {
                        currentViewGroup.views.push(view);
                    } else {
                        viewListByCollection.push({ id: collectionId, title: collectionName, views: [view] });
                    }
                }

                return view;
            }) || [];

        const merchantViews =
            merchantDisplayPreferences?.map((displayPreference) => {
                const name = displayPreference.get("name");
                const id = displayPreference.get("id");
                const collectionId = displayPreference.get("collectionId");

                return {
                    ...displayPreference.toJS(),
                    lowerCaseName: name.toLowerCase(),
                    preference: displayPreference,
                    collectionId,
                    name,
                    id,
                };
            }) || [];

        return [queryViewId || defaultViewId, views, merchantViews, viewListByCollection];
    }, [displayPreferences, merchantDisplayPreferences, queryViewId, isRollupMode]);

    React.useEffect(() => {
        if (rollupViews) {
            const updatedDisplayPreferences = [...itemDisplayPreferences];

            itemDisplayPreferences.forEach((displayPreference, index) => {
                const displayPreferenceId: string = displayPreference.get("id");
                const rollupView = rollupViews[displayPreferenceId];

                if (rollupView) {
                    const rollupViewDisplayPreference: List<Map<string, any>> = rollupView.get("display_preferences");
                    const currentDisplayPreference: List<Map<string, any>> =
                        displayPreference.get("display_preferences");
                    const firstDisplayPreferenceField = currentDisplayPreference.get(0);
                    const mergedFields = rollupViewDisplayPreference.splice(0, 0, firstDisplayPreferenceField);
                    let updatedDisplayPreference = displayPreference.mergeDeep(rollupView);
                    updatedDisplayPreference = updatedDisplayPreference.set("display_preferences", mergedFields);
                    updatedDisplayPreferences[index] = updatedDisplayPreference;
                }
            });

            setDisplayPreferences(updatedDisplayPreferences);
        }
    }, [rollupViews]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (
            isRollupMode &&
            displayPreferences &&
            List.isList(displayPreferences) &&
            (displayPreferences as any).size > 0 &&
            merchantDisplayPreferences &&
            List.isList(merchantDisplayPreferences) &&
            (merchantDisplayPreferences as any).size > 0 &&
            !mergedMerchantDisplayPreferences
        ) {
            const updatedMerchantDisplayPreferences = [...merchantDisplayPreferences];
            const firstClonedDisplayPreferenceForRollup = lodashCloneDeep(
                (displayPreferences as any).get(0).get("display_preferences").get(0)
            );
            merchantDisplayPreferences.forEach((displayPreference, index) => {
                const updatedDisplayPreferences = displayPreference
                    .get("display_preferences")
                    .splice(1, 0, firstClonedDisplayPreferenceForRollup);
                updatedMerchantDisplayPreferences[index] = displayPreference.set(
                    "display_preferences",
                    updatedDisplayPreferences
                );
            });

            if (Array.isArray(updatedMerchantDisplayPreferences)) {
                setMerchantDisplayPreferences(fromJS(updatedMerchantDisplayPreferences));
            } else {
                setMerchantDisplayPreferences(updatedMerchantDisplayPreferences);
            }

            setMergedDisplayPreferences(true);
        }
    }, [displayPreferences, merchantDisplayPreferences, isRollupMode, mergedMerchantDisplayPreferences]);

    React.useEffect(() => {
        if (switchingTile && requestedView) {
            handleLoad(
                requestedView.viewName,
                requestedView.viewId,
                requestedView.viewIndex,
                requestedView.collectionId
            );
            setRequestedView(undefined);
        }
        handleSwitchingTileAndGrid(true);
    }, [displayPreferences, merchantDisplayPreferences]); // eslint-disable-line react-hooks/exhaustive-deps

    // If the viewId is a query parameter, check to see if it still exists within the list and set it the current state to the correct view or remove it from query.
    //TODO REFACTOR: MOVE THIS TO A CUSTOM HOOK AND SEPARATE THE LOGICS FOR ROLLUP AND NORMAL ASSORTMENT
    React.useEffect(() => {
        /// this hooks handles loading views when a user defined view(not the default one) is applied
        const { viewId, ...query } = router.location.query;
        if (viewId) {
            let initialView;
            if (isRollupMode) {
                const [selectedViewId, selectedCollectionId] = viewId.split(CollectionSavedViewRollupSeparator);
                const viewIndex = viewList.findIndex(
                    (view) => view.id === selectedViewId && view.collectionId === selectedCollectionId
                );
                initialView = (viewList as any).get(viewIndex);
                if (!isPreferenceLoaded(initialView)) {
                    onFetchRollupItemDisplayPreferences(initialView.ref);
                    setRequestedView({
                        viewName: initialView.name,
                        viewId: selectedViewId,
                        viewIndex,
                        collectionId: selectedCollectionId,
                    });
                    return;
                }
            } else {
                initialView = [...viewList, ...merchantViewList].find((view) => view.id === viewId);
            }
            if (initialView) {
                const { name, preference } = initialView;

                const propertiesToLoad = preference.get("display_preferences")
                    ? amphibiousToJS(preference.get("display_preferences"))
                    : [];

                const gridParams: IGridParams = preference.get("grid_parameters")
                    ? amphibiousToJS(preference.get("grid_parameters"))
                    : {};

                const dimensionPreferences: IServerDimensionPreference =
                    preference && preference.get("allocation_dimensions")
                        ? amphibiousToJS(preference.get("allocation_dimensions"))
                        : {};
                const currentViewMetrics = preference.get("door_group_metrics")
                    ? amphibiousToJS(preference.get("door_group_metrics"))
                    : [];
                const latestSchemaMetrics = doorGroupMetricsSchema.toJS();

                const parsedMetrics = parseMetrics(currentViewMetrics, latestSchemaMetrics);

                const { schema, propertiesWereRemoved } = mergeWithDefaultSchema(
                    propertiesToLoad,
                    itemDisplayProperty,
                    name !== DEFAULT_VIEW_NAME,
                    name === DEFAULT_VIEW_NAME
                );
                if (propertiesWereRemoved.length) {
                    setSchemaIdWithRemovedFields(initialView.id);
                    setRemovedSchemaFields(propertiesWereRemoved);
                    if (!isRollupMode) {
                        setIsRemovedFieldsModalOpen(true);
                    }
                }
                setViewParams({ gridParams, dimensionPreferences, metrics: parsedMetrics });
                onChangeCurrentView(isRollupMode ? `${name}${initialView.id}` : name, schema);
            } else {
                router.push({ pathname: router.location.pathname, query: { ...query, viewId: undefined } });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemDisplayProperty]);

    React.useEffect(() => {
        const { viewId } = router.location.query;
        const defaultViewAssortment = currentViewName === DEFAULT_MASTER_VIEW_NAME && !isRollupMode && !viewId;
        const defaultViewRollup = !viewId && isRollupMode;

        const mergedDimensionPreference = mergeDimensionPreference(
            defaultAllocationDimensions,
            dimensionsPanelApplied,
            merchantAllocationDimensions
        );

        //Resetting the image width to the default when the default view is loaded
        if (!viewId && onSetImageCellDimensions) {
            onSetImageCellDimensions({ width: IMAGE_COLUMN_MIN_WIDTH, height: ROW_HEIGHT });
        }

        if (defaultViewAssortment) {
            const defaultView = viewList.find((viewItem) => viewItem.name === DEFAULT_VIEW_NAME);
            const preference = defaultView?.preference;
            const propertiesToLoad =
                preference && preference.get("display_preferences")
                    ? amphibiousToJS(preference.get("display_preferences"))
                    : [];
            const gridParams: IGridParams =
                preference && preference.get("grid_parameters")
                    ? amphibiousToJS(preference.get("grid_parameters"))
                    : {};

            const { schema, changeDetected: itemDisplayPropertyChangeDetected } = mergeWithDefaultSchema(
                propertiesToLoad,
                itemDisplayProperty,
                false,
                !!defaultView
            );

            if (editable) {
                itemDisplayPropertyChangeDetected &&
                    onUpdate(displayPreferencesId, 0, {
                        id: defaultView.id,
                        name: DEFAULT_VIEW_NAME,
                        display_preferences: schema.toJS(),
                        grid_parameters: gridParams,
                    });

                const viewParamsToUpdate = {
                    gridParams,
                    dimensionPreferences: undefined,
                    metrics: undefined,
                    shouldLoadNewSavedView: true,
                };

                if (mergedDimensionPreference) {
                    viewParamsToUpdate.dimensionPreferences = mergedDimensionPreference;
                }

                if (metricsFromSchema) {
                    viewParamsToUpdate.metrics = metricsFromSchema;
                }

                setViewParams(viewParamsToUpdate);
            }

            onChangeCurrentView(DEFAULT_VIEW_NAME, schema);
        } else if (defaultViewRollup && mergedDimensionPreference) {
            setViewParams({ dimensionPreferences: mergedDimensionPreference, metrics: metricsFromSchema });
        }
    }, [currentViewName, isRollupMode, merchantAllocationDimensions, itemDisplayProperty, metricsFromSchema]); // eslint-disable-line react-hooks/exhaustive-deps

    // Get merchant saved views for this merchant if this is a rollup
    React.useEffect(() => {
        if (merchantId && isRollupMode && !fetchedMerchantDisplayPreferences) {
            onFetchMerchantPreferences(merchantId);
            setFetchedMerchantDisplayPreferences(true);
        }
    }, [merchantId, isRollupMode, onFetchMerchantPreferences, fetchedMerchantDisplayPreferences]);

    const setViewParams = ({
        gridParams,
        dimensionPreferences,
        shouldLoadNewSavedView,
        metrics,
    }: {
        gridParams?: IGridParams;
        dimensionPreferences: IServerDimensionPreference;
        metrics?: Metrics;
        shouldLoadNewSavedView?: boolean;
    }): void => {
        const gridParamsForGrid = gridParams && transformGridParamsForGrid(gridParams);

        // Default to current dimensions state
        let allocationDimensionsForGrid = dimensionsPanelApplied;

        if (dimensionPreferences) {
            allocationDimensionsForGrid = transformAllocationDimensionForGrid({
                allocationDimensions: defaultAllocationDimensions,
                dimensionPreferences,
                metrics,
            });
        }

        // Exclude plan rows from saved views. When applying a view that includes Plan rows, apply all aspects of the view except the Plan rows.
        if (isRollupMode) {
            allocationDimensionsForGrid.metrics.showAssortmentPlan = false;
        }

        onViewParamsChange(
            gridParamsForGrid,
            allocationDimensionsForGrid,
            gridParams?.search_term,
            shouldLoadNewSavedView
        );
    };

    const handleNewView = React.useCallback(() => {
        setEditedViewName("");
        setIsEditOpen(true);
    }, []);

    const handleDelete = React.useCallback(
        (viewId, viewIndex, savedView) => {
            if (viewIndex >= 0) {
                const analytics = (global as any).analytics as any;
                analytics.track(SavedViewsActions.Remove, {
                    id: savedView.id,
                    name: savedView.name,
                });
                onRemove(displayPreferencesId, viewIndex);
            }

            if (viewId === router.location.query.viewId) {
                onSetViewToDefault();
                onClearViewParams();
                router.push({
                    pathname: router.location.pathname,
                    query: { ...router.location.query, viewId: undefined },
                });
            }
        },
        [displayPreferencesId, onSetViewToDefault, onRemove, router, onClearViewParams]
    );

    const handleCancelEdit: React.EventHandler<React.MouseEvent<HTMLElement>> = React.useCallback((_e) => {
        setIsEditOpen(false);
    }, []);

    const handleUpdate = React.useCallback(
        (viewId, viewName, viewIndex) => {
            const analytics = (global as any).analytics as any;
            analytics.track(SavedViewsActions.Update, {
                id: viewId,
                name: viewName,
            });

            const gridParams = getGridParams();
            onUpdate(displayPreferencesId, viewIndex, {
                id: viewId,
                name: viewName,
                display_preferences: currentView.toJS(),
                grid_parameters: transformGridParamsForUpdate(gridParams),
                allocation_dimensions: transformAllocationDimensionForUpdate(
                    dimensionsPanelApplied,
                    defaultAllocationDimensions.map((dim) => dim.id)
                ),
                // We don't want to store legacy metrics
                door_group_metrics: isMetricsEnabled
                    ? transformDoorGroupMetricsForUpdate(dimensionsPanelApplied.metrics.values)
                    : [],
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            currentView,
            getGridParams,
            displayPreferencesId,
            onUpdate,
            dimensionsPanelApplied,
            defaultAllocationDimensions,
            isMetricsEnabled,
        ]
    );

    const handlePromote = React.useCallback(
        (viewId, viewName, viewIndex) => {
            WindowAnalytics.track(SavedViewsActions.Promote, {
                id: viewId,
                name: viewName,
            });

            setConfirmPromoteSavedViewModalInfo({
                viewId,
                viewName,
                viewIndex,
            });
        },
        [setConfirmPromoteSavedViewModalInfo]
    );

    const doPromotion = React.useCallback(() => {
        if (confirmPromoteSavedViewModalInfo) {
            const { viewIndex } = confirmPromoteSavedViewModalInfo;
            onPromote(collectionId, merchantId, viewIndex);
            setConfirmPromoteSavedViewModalInfo(null);
        }
    }, [collectionId, confirmPromoteSavedViewModalInfo, merchantId, onPromote]);

    const triggerMerchantViewUpdate = React.useCallback(
        (viewId, viewName, viewIndex) => {
            WindowAnalytics.track(SavedViewsActions.MerchantUpdate, {
                id: viewId,
                name: viewName,
            });

            setUpdateSavedViewModalInfo({
                viewId,
                viewName,
                viewIndex,
            });
        },
        [setUpdateSavedViewModalInfo]
    );

    const updateMerchantView = React.useCallback(
        (merchantId, viewId, viewName, viewIndex, isSuperUser) => {
            const gridParams = getGridParams();

            const displayPreference = {
                id: viewId,
                name: viewName,
                display_preferences: currentView.toJS(),
                grid_parameters: transformGridParamsForUpdate(gridParams),
                allocation_dimensions: transformAllocationDimensionForUpdate(
                    dimensionsPanelApplied,
                    defaultAllocationDimensions.map((dim) => dim.id)
                ),
                // We don't want to store legacy metrics
                door_group_metrics: isMetricsEnabled
                    ? transformDoorGroupMetricsForUpdate(dimensionsPanelApplied.metrics.values)
                    : [],
            };

            if (isSuperUser) {
                onMerchantUpdate(merchantId, viewId, {
                    item_display_preference: displayPreference,
                });
            }
            onMerchantLocalUpdate(merchantDisplayPreferencesId, viewIndex, displayPreference);
        },
        [
            currentView,
            defaultAllocationDimensions,
            dimensionsPanelApplied,
            getGridParams,
            merchantDisplayPreferencesId,
            onMerchantLocalUpdate,
            onMerchantUpdate,
            isMetricsEnabled,
        ]
    );

    const doMerchantViewUpdateAsSuperUser = () => {
        if (confirmUpdateSavedViewModalInfo) {
            const { viewId, viewName, viewIndex } = confirmUpdateSavedViewModalInfo;
            updateMerchantView(merchantId, viewId, viewName, viewIndex, true);
            setUpdateSavedViewModalInfo(null);
        }
    };

    const handleMerchantLocalViewUpdate = (viewId, viewName, viewIndex) => {
        WindowAnalytics.track(SavedViewsActions.MerchantUpdateLocal, {
            id: viewId,
            name: viewName,
        });

        updateMerchantView(merchantId, viewId, viewName, viewIndex, false);
    };

    const handleMerchantViewDelete = React.useCallback(
        (viewId, viewName, viewIndex) => {
            WindowAnalytics.track(SavedViewsActions.MerchantDelete, {
                id: viewId,
                name: viewName,
            });

            setDeleteSavedViewModalInfo({
                viewId,
                viewName,
                viewIndex,
            });
        },
        [setDeleteSavedViewModalInfo]
    );

    const doMerchantViewDelete = React.useCallback(() => {
        if (confirmDeleteSavedViewModalInfo) {
            const { viewId, viewIndex } = confirmDeleteSavedViewModalInfo;
            onMerchantDelete(collectionId, merchantId, viewId, viewIndex);
            setDeleteSavedViewModalInfo(null);
        }
    }, [confirmDeleteSavedViewModalInfo, collectionId, merchantId, onMerchantDelete]);

    const handleReset = React.useCallback(
        (viewId, viewName, viewIndex) => {
            const analytics = (global as any).analytics as any;
            analytics.track(SavedViewsActions.Reset, {
                id: viewId,
                name: viewName,
            });

            onUpdate(displayPreferencesId, viewIndex, {
                id: viewId,
                name: viewName,
                display_preferences: itemDisplayProperty.toJS(),
                grid_parameters: {
                    aggregations: [],
                    pivot: { mode: false, columns: [] },
                    row_groups: [],
                },
                allocation_dimensions: {
                    dimensions: [],
                    expanded_columns: {},
                },
            });
            onChangeCurrentView(viewName, itemDisplayProperty);
            onClearViewParams();
            onSetImageCellDimensions({ width: IMAGE_COLUMN_MIN_WIDTH, height: ROW_HEIGHT });
        },
        [
            displayPreferencesId,
            onUpdate,
            itemDisplayProperty,
            onChangeCurrentView,
            onClearViewParams,
            onSetImageCellDimensions,
        ]
    );

    const handleSaveNew = React.useCallback(() => {
        const viewId = uuid();
        const nameMatchFound = ([...displayPreferences, ...(merchantDisplayPreferences || [])] || []).find(
            (pref) => pref.get("name").trim().toLowerCase() === editedViewName.trim().toLowerCase()
        );
        if (
            nameMatchFound ||
            editedViewName.trim().toLowerCase() === DEFAULT_VIEW_NAME.trim().toLowerCase() ||
            editedViewName.trim().toLowerCase() === ""
        ) {
            setViewNameUnique(false);
        } else {
            const analytics = (global as any).analytics as any;
            analytics.track(SavedViewsActions.Create, {
                id: viewId,
                name: editedViewName,
            });

            const gridParams = getGridParams();
            onAdd(
                displayPreferencesId,
                {
                    id: viewId,
                    name: editedViewName,
                    display_preferences: currentView.toJS(),
                    grid_parameters: transformGridParamsForUpdate(gridParams),
                    allocation_dimensions: transformAllocationDimensionForUpdate(
                        dimensionsPanelApplied,
                        defaultAllocationDimensions.map((dim) => dim.id)
                    ),
                    // We don't want to store legacy metrics
                    door_group_metrics: isMetricsEnabled
                        ? transformDoorGroupMetricsForUpdate(dimensionsPanelApplied.metrics.values)
                        : [],
                },
                (viewList as any).size
            );
            onChangeCurrentView(editedViewName);
            setIsEditOpen(false);
            router.push({ pathname: router.location.pathname, query: { ...router.location.query, viewId } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        onAdd,
        displayPreferencesId,
        editedViewName,
        currentView,
        getGridParams,
        router,
        displayPreferences,
        merchantDisplayPreferences,
        viewList,
        defaultAllocationDimensions,
        dimensionsPanelApplied,
        isMetricsEnabled,
    ]);

    handleSaveNewRef.current = handleSaveNew;

    React.useEffect(() => {
        if (debouncedEnterKeyDown === "true") {
            handleSaveNewRef.current();
            setEnterKeyDown("false");
        }
    }, [debouncedEnterKeyDown]);

    React.useEffect(() => {
        if (debouncedSaveButtonClicked === "true") {
            handleSaveNewRef.current();
            setSaveButtonClicked("false");
        }
    }, [debouncedSaveButtonClicked]);

    const handleLoad = React.useCallback(
        (viewName, viewId, viewIndex, collectionId?) => {
            SentryUtil.startTransaction(AssortmentTransactions.SwitchView);
            SentryUtil.startSpan(AssortmentTransactions.SwitchView, AssortmentEvents.SwitchView);
            const analytics = (global as any).analytics as any;
            analytics.track(SavedViewsActions.Open, {
                id: viewId,
                name: viewName,
                isRollupMode,
            });

            let preference: Map<string, any>;

            if (isRollupMode && collectionId) {
                preference = [...displayPreferences, ...(merchantDisplayPreferences || [])].find(
                    (view) => view.get("id") === viewId && view.get("collectionId") === collectionId
                );
                if (!isPreferenceLoaded(preference.toJS())) {
                    onFetchRollupItemDisplayPreferences(preference.get("ref")?.toJS());
                    setRequestedView({ viewName, viewId, viewIndex, collectionId });
                    return;
                }
            } else {
                preference = [...displayPreferences, ...(merchantDisplayPreferences || [])].find(
                    (view) => view.get("id") === viewId
                );
            }

            if (!preference) {
                return;
            }

            const gridParams: IGridParams = preference.get("grid_parameters")
                ? amphibiousToJS(preference.get("grid_parameters"))
                : {};
            const dimensionPreferences: IServerDimensionPreference = preference.get("allocation_dimensions")
                ? amphibiousToJS(preference.get("allocation_dimensions"))
                : {};
            const propertiesToLoad = preference.get("display_preferences")
                ? amphibiousToJS(preference.get("display_preferences"))
                : [];
            const currentViewMetrics = preference.get("door_group_metrics")
                ? amphibiousToJS(preference.get("door_group_metrics"))
                : [];
            const latestSchemaMetrics = doorGroupMetricsSchema.toJS();

            const parsedMetrics = parseMetrics(currentViewMetrics, latestSchemaMetrics);

            setViewParams({ gridParams, dimensionPreferences, metrics: parsedMetrics, shouldLoadNewSavedView: true });
            const {
                schema,
                propertiesWereRemoved,
                changeDetected: itemDisplayPropertyChangeDetected,
            } = mergeWithDefaultSchema(
                propertiesToLoad,
                itemDisplayProperty,
                viewName !== DEFAULT_VIEW_NAME,
                viewName === DEFAULT_VIEW_NAME,
                onSetImageCellDimensions
            );

            if (propertiesWereRemoved.length) {
                setSchemaIdWithRemovedFields(viewId);
                setRemovedSchemaFields(propertiesWereRemoved);
                if (!isRollupMode) {
                    setIsRemovedFieldsModalOpen(true);
                }
            }

            if (isRollupMode) {
                onChangeCurrentView(`${viewName}${viewId}`, schema);
            } else if (!isRollupMode && viewName === DEFAULT_VIEW_NAME) {
                onChangeCurrentView(DEFAULT_MASTER_VIEW_NAME);
            } else {
                onChangeCurrentView(viewName, schema);
            }

            if (!isRollupMode && viewName === DEFAULT_VIEW_NAME && itemDisplayPropertyChangeDetected) {
                const gridParamsForUpdate = getGridParams();

                onUpdate(displayPreferencesId, viewIndex, {
                    id: viewId,
                    name: viewName,
                    display_preferences: currentView.toJS(),
                    grid_parameters: transformGridParamsForUpdate(gridParamsForUpdate),
                    allocation_dimensions: transformAllocationDimensionForUpdate(
                        dimensionsPanelApplied,
                        defaultAllocationDimensions.map((dim) => dim.id)
                    ),
                    // We don't want to store legacy metrics
                    door_group_metrics: isMetricsEnabled
                        ? transformDoorGroupMetricsForUpdate(dimensionsPanelApplied.metrics.values)
                        : [],
                });
            }

            let viewIdValue;

            if (isRollupMode && preference.get("collectionId")) {
                viewIdValue = `${viewId}${CollectionSavedViewRollupSeparator}${preference.get("collectionId")}`;
            } else {
                viewIdValue = viewName === DEFAULT_VIEW_NAME ? undefined : viewId;
            }

            router.push({
                pathname: router.location.pathname,
                query: { ...router.location.query, viewId: viewIdValue },
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            displayPreferences,
            merchantDisplayPreferences,
            mergeWithDefaultSchema,
            router,
            itemDisplayProperty,
            getGridParams,
            dimensionsPanelApplied,
            defaultAllocationDimensions,
            isRollupMode,
            isMetricsEnabled,
        ]
    );

    const handleEditTitle: React.EventHandler<React.ChangeEvent<HTMLInputElement>> = React.useCallback((e) => {
        setViewNameUnique(true);
        setEditedViewName(e.target.value);
    }, []);

    const handleKeyDown = React.useCallback(
        (e) => {
            if (e.keyCode === 13) {
                setEnterKeyDown("true");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleSaveNew]
    );

    const handleSaveButtonClicked = React.useCallback(() => {
        SentryUtil.startTransaction(AssortmentTransactions.SaveView);
        SentryUtil.startSpan(AssortmentTransactions.SaveView, AssortmentEvents.SaveView, { viewName: editedViewName });
        setSaveButtonClicked("true");
    }, [editedViewName]);

    const handleOpenRemovedFieldModal = React.useCallback(
        (e) => {
            if (removedSchemaFields.length && !isRollupMode) {
                e.preventDefault();
                setIsRemovedFieldsModalOpen(true);
            }
        },
        [removedSchemaFields, isRollupMode]
    );

    const handleCloseRemoveFieldModal = React.useCallback(() => {
        setIsRemovedFieldsModalOpen(false);
    }, []);

    const handleCloseConfirmPromoteSavedViewModal = React.useCallback(() => {
        setConfirmPromoteSavedViewModalInfo(null);
    }, [setConfirmPromoteSavedViewModalInfo]);

    const handleCloseConfirmUpdateMerchantSavedViewModal = React.useCallback(() => {
        setUpdateSavedViewModalInfo(null);
    }, [setUpdateSavedViewModalInfo]);

    const handleCloseConfirmDeleteMerchantSavedViewModal = React.useCallback(() => {
        setDeleteSavedViewModalInfo(null);
    }, [setDeleteSavedViewModalInfo]);

    const handleUpdateRemoveFields = React.useCallback(() => {
        const { viewId } = router.location.query;
        const isNuOrderUser = me?.isNuOrderUser();

        let index = displayPreferences.findIndex((pref) => pref.get("id") === viewId);
        let updateListName;
        const { schema } = mergeWithDefaultSchema(
            currentView.toJS(),
            itemDisplayProperty,
            false,
            updateListName === DEFAULT_VIEW_NAME
        );
        const gridParams = getGridParams();

        const updatedDisplayPreference = {
            id: viewId,
            name: updateListName,
            display_preferences: schema.toJS(),
            grid_parameters: transformGridParamsForUpdate(gridParams),
            allocation_dimensions: transformAllocationDimensionForUpdate(
                dimensionsPanelApplied,
                defaultAllocationDimensions.map((dim) => dim.id)
            ),
            // We don't want to store legacy metrics
            door_group_metrics: isMetricsEnabled
                ? transformDoorGroupMetricsForUpdate(dimensionsPanelApplied.metrics.values)
                : [],
        };

        if (index >= 0) {
            // Update regular saved views
            updatedDisplayPreference.name = (displayPreferences as any).get(index).get("name");

            onUpdate(displayPreferencesId, index, updatedDisplayPreference);
        } else if (index < 0 && merchantDisplayPreferences) {
            // Update merchant saved views
            // If it's not found in display preferences, find it in merchant preferences
            index = merchantDisplayPreferences.findIndex((pref) => pref.get("id") === viewId);
            if (index >= 0) {
                updatedDisplayPreference.name = (merchantDisplayPreferences as any).get(index).get("name");
                if (isNuOrderUser) {
                    onMerchantUpdate(merchantId, viewId, {
                        item_display_preference: updatedDisplayPreference,
                    });
                }
                onMerchantLocalUpdate(merchantDisplayPreferencesId, index, updatedDisplayPreference);
            }
        }

        setSchemaIdWithRemovedFields(null);
        setRemovedSchemaFields([]);
        onChangeCurrentView(isRollupMode ? `${updateListName}${viewId}` : updateListName, schema);
        setIsRemovedFieldsModalOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        mergeWithDefaultSchema,
        editedViewName,
        onUpdate,
        displayPreferencesId,
        displayPreferences,
        merchantDisplayPreferences,
        router,
        currentView,
        router,
        itemDisplayProperty,
        getGridParams,
        dimensionsPanelApplied,
        defaultAllocationDimensions,
        merchantId,
        isMetricsEnabled,
    ]);

    const handleLoadDefaultMaster = React.useCallback(() => {
        onSetViewToDefault();
        onClearViewParams();
        router.push({ pathname: router.location.pathname, query: { ...router.location.query, viewId: undefined } });
    }, [onSetViewToDefault, router, onClearViewParams]);

    return (
        <div {...styles}>
            {editable && (
                <div className="newView">
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_SAVE_CURRENT}
                        className="newViewButton"
                        variant="outlined"
                        color="primary"
                        onClick={handleNewView}
                    >
                        {formatMessage("Save current view")}
                    </Button>
                </div>
            )}
            <div data-test={DATATEST.ASSORTMENTS.SAVED_VIEWS_LIST} className="menuList">
                <Divider />
                {isRollupMode ? (
                    <SavedViewsByCollection
                        currentViewId={currentViewId}
                        schemaIdWithRemovedFields={schemaIdWithRemovedFields}
                        onLoad={handleLoad}
                        handleLoadDefaultMaster={handleLoadDefaultMaster}
                        onOpenRemovedFieldModal={handleOpenRemovedFieldModal}
                        editable={editable}
                        isDefaultMasterViewActive={currentViewName === DEFAULT_MASTER_VIEW_NAME}
                        items={viewListByCollection}
                        isReviewTab={false}
                        // @ts-expect-error we need to type viewList to avoid this error, but this would require a more indeep refactor on this whole component
                        merchantItems={merchantViewList}
                    />
                ) : (
                    <SavedViews
                        currentViewId={currentViewId}
                        schemaIdWithRemovedFields={schemaIdWithRemovedFields}
                        onLoad={handleLoad}
                        onUpdate={handleUpdate}
                        onMerchantViewUpdate={triggerMerchantViewUpdate}
                        onMerchantLocalViewUpdate={handleMerchantLocalViewUpdate}
                        onMerchantViewDelete={handleMerchantViewDelete}
                        onPromote={handlePromote}
                        onReset={handleReset}
                        onDelete={handleDelete}
                        onNewView={handleNewView}
                        onOpenRemovedFieldModal={handleOpenRemovedFieldModal}
                        editable={editable}
                        // @ts-expect-error we need to type viewList to avoid this error, but this would require a more indeep refactor on this whole component
                        items={viewList}
                        merchantItems={merchantViewList}
                    />
                )}
            </div>
            <Dialog
                open={isEditOpen}
                onRequestClose={handleCancelEdit}
                title={formatMessage("Save new view")}
                contentClassName={`${dialogStyle}`}
                actions={[
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_CANCEL}
                        key="newViewBtnCancel"
                        label={formatMessage("Cancel")}
                        onClick={handleCancelEdit}
                        className="btnCancel"
                    />,
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_SAVE}
                        key="newViewBtnSave"
                        label={formatMessage("Save")}
                        onClick={handleSaveButtonClicked}
                        primary={true}
                    />,
                ]}
            >
                <div className="saveViewBody">
                    <Form>
                        <TextField
                            dataTest={DATATEST.ASSORTMENTS.VIEWS_NAME_INPUT}
                            large={false}
                            fullWidth={true}
                            label={formatMessage("View Name")}
                            onChange={handleEditTitle}
                            onKeyDown={handleKeyDown}
                            name="viewName"
                            errorText={
                                !viewNameUnique ? formatMessage("View names must be unique and not blank") : undefined
                            }
                            errorStyle={{ marginTop: "8px" }}
                        />
                    </Form>
                </div>
            </Dialog>
            <Dialog
                open={isRemovedFieldsModalOpen}
                onRequestClose={handleCloseRemoveFieldModal}
                title={formatMessage("View update")}
                contentClassName={`${dialogStyle}`}
                autoScrollBodyContent={true}
                actions={[
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_CANCEL}
                        key="updateBtnCancel"
                        label={formatMessage("Cancel")}
                        onClick={handleCloseRemoveFieldModal}
                        className="btnCancel"
                    />,
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_SAVE}
                        key="updateBtnSave"
                        label={formatMessage("Save")}
                        onClick={handleUpdateRemoveFields}
                        primary={true}
                    />,
                ]}
            >
                <div className="removedModalBody">
                    <Typography variant="h6">
                        {formatMessage(
                            "The following fields were removed off the master view. To update your view to also remove these fields, click Save."
                        )}
                    </Typography>
                    <Typography variant="h5" className="removedFields">
                        {removedSchemaFields.join(", ")}
                    </Typography>
                </div>
            </Dialog>
            <Dialog
                open={confirmPromoteSavedViewModalInfo !== null}
                onRequestClose={handleCloseConfirmPromoteSavedViewModal}
                title={formatMessage("Confirm Promotion")}
                contentClassName={`${dialogStyle}`}
                autoScrollBodyContent={true}
                actions={[
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_CANCEL}
                        key="promoteBtnCancel"
                        label={formatMessage("Cancel")}
                        onClick={handleCloseConfirmPromoteSavedViewModal}
                        className="btnCancel"
                    />,
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_PROMOTE}
                        key="promoteBtnSave"
                        label={formatMessage("Promote")}
                        onClick={doPromotion}
                        primary={true}
                    />,
                ]}
            >
                <div className="removedModalBody">
                    <Typography variant="h6">
                        {formatMessage(
                            "Are you sure you want to promote this view to a Merchant-level saved view? This will include the view in all newly created assortments."
                        )}
                    </Typography>
                </div>
            </Dialog>
            <Dialog
                open={confirmUpdateSavedViewModalInfo !== null}
                onRequestClose={handleCloseConfirmUpdateMerchantSavedViewModal}
                title={formatMessage("Confirm Update Merchant Saved View")}
                contentClassName={`${dialogStyle}`}
                autoScrollBodyContent={true}
                actions={[
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_CANCEL}
                        key="merchantBtnCancel"
                        label={formatMessage("Cancel")}
                        onClick={handleCloseConfirmUpdateMerchantSavedViewModal}
                        className="btnCancel"
                    />,
                    <Button
                        data-test={DATATEST.ASSORTMENTS.MERCHANT_SAVED_VIEWS_UPDATE}
                        key="merchantUpdateBtn"
                        label={formatMessage("Update")}
                        onClick={doMerchantViewUpdateAsSuperUser}
                        primary={true}
                    />,
                ]}
            >
                <div className="removedModalBody">
                    <Typography variant="h6">
                        {formatMessage(
                            "Are you sure you want to update this Merchant-level saved view for all users? This will include the updated version of this view in all newly created assortments."
                        )}
                    </Typography>
                </div>
            </Dialog>
            <Dialog
                open={confirmDeleteSavedViewModalInfo !== null}
                onRequestClose={handleCloseConfirmDeleteMerchantSavedViewModal}
                title={formatMessage("Confirm Delete Merchant Saved View")}
                contentClassName={`${dialogStyle}`}
                autoScrollBodyContent={true}
                actions={[
                    <Button
                        data-test={DATATEST.ASSORTMENTS.VIEWS_CANCEL}
                        key="merchantBtnCancel"
                        label={formatMessage("Cancel")}
                        onClick={handleCloseConfirmDeleteMerchantSavedViewModal}
                        className="btnCancel"
                    />,
                    <Button
                        data-test={DATATEST.ASSORTMENTS.MERCHANT_SAVED_VIEWS_DELETE}
                        key="merchantDeleteBtn"
                        label={formatMessage("Delete")}
                        onClick={doMerchantViewDelete}
                        primary={true}
                    />,
                ]}
            >
                <div className="removedModalBody">
                    <Typography variant="h6">
                        {formatMessage(
                            "Are you sure you want to delete this Merchant-level saved view? This will exclude the view from all newly created assortments."
                        )}
                    </Typography>
                </div>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (state: IStore, { collectionId, isRollupMode }: IOwnProps): IStateProps => {
    const collection = state.mutate.snapshots && state.mutate.snapshots.get(collectionId);
    const displayPreferencesId: string = collection
        ? collection.getIn(["item_display_preference_object_id", "ref", "object_id"])
        : undefined;
    const merchantDisplayPreferencesId: string = collection
        ? collection.getIn(["merchant_item_display_preference_object_id", "ref", "object_id"])
        : undefined;
    const merchantId: string = collection ? collection.getIn(["merchant_v1_id", "string_value"]) : undefined;
    const itemDisplayPreferences = state.mutate.snapshots && state.mutate.snapshots.get(displayPreferencesId);

    const merchantItemDisplayPreferences = merchantDisplayPreferencesId
        ? state.mutate?.snapshots?.get(merchantDisplayPreferencesId)
        : isRollupMode
          ? state.collectionItemDisplayPreference.rollupMerchantSavedViews
          : undefined;

    const itemDisplayProperty: List<Map<keyof ISchemaKey, any>> = collection
        ? collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS)
        : undefined;
    const {
        collectionItemDisplayPreference: { currentView, currentViewName },
        collectionRollupObjects: { objects },
    } = state;
    const defaultAllocationDimensions = getAllocationDimensionsBySubtype(
        state,
        collectionId,
        AllocationDimensionKind.Generic
    );
    const doorGroupMetricsSchema = collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.DOOR_GROUP_METRICS);
    return {
        itemDisplayPreferences,
        merchantItemDisplayPreferences,
        merchantId,
        displayPreferencesId,
        merchantDisplayPreferencesId,
        currentViewName,
        currentView,
        itemDisplayProperty,
        defaultAllocationDimensions,
        merchantAllocationDimensions: state.merchantDefaultAllocationDimensionsPreference?.result,
        doorGroupMetricsSchema,
        rollupViews: objects,
        dimensionsPanelApplied: state.dimensionsPanel.applied,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IAction<any>>, { collectionId }: IOwnProps): IDispatchProps => {
    return {
        onAdd: (id: string, displayPreference: IDisplayPreference, index: number) =>
            dispatch(addItemDisplayPreferences(id, displayPreference, index)),
        onRemove: (id: string, index: number) => dispatch(removeItemDisplayPreferences(id, index)),
        onUpdate: (id: string, index: number, displayPreference: IDisplayPreference) =>
            dispatch(editItemDisplayPreference(id, index, displayPreference)),
        onMerchantUpdate: (merchantId: string, id: string, displayPreference: IMerchantDisplayPreference) =>
            dispatch(editMerchantItemDisplayPreference(merchantId, id, displayPreference)),
        onMerchantDelete: (collectionId: string, merchantId: string, id: string, index: number) =>
            dispatch(deleteMerchantItemDisplayPreference(collectionId, merchantId, id, index)),
        onMerchantLocalUpdate: (id: string, index: number, displayPreference: IDisplayPreference) =>
            dispatch(editMerchantLocalItemDisplayPreference(id, index, displayPreference)),
        onPromote: (collectionId: string, merchantId: string, index: number) =>
            dispatch(promoteItemDisplayPreference(collectionId, merchantId, index)),
        onFetchMerchantPreferences: (merchantId: string) => dispatch(fetchMerchantItemDisplayPreferences(merchantId)),
        onChangeCurrentView: (name: string, data: List<Map<string, any>>) =>
            dispatch(changeCurrentView({ name, data })),
        onSetViewToDefault: () => dispatch(setViewToDefault(collectionId)),
        onFetchRollupItemDisplayPreferences: (params) => dispatch(fetchRollupObject(params)),
    };
};

export const getDataViewPanelComponent = (
    mapStateToProps: (state: IStore, { collectionId }: IOwnProps) => any,
    mapDispatchToProps: (dispatch: Dispatch<IAction<any>>, { collectionId }: IOwnProps) => any
) =>
    withRouter(
        connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(DataViewPanelComponent)
    );

export const DataViewPanel = getDataViewPanelComponent(mapStateToProps, mapDispatchToProps);
